/**
 * Jobs data table (RM-118). shadcn Table + @tanstack/react-table: sortable
 * columns, a column-visibility picker, and client-side pagination. The column
 * config (visibility + sorting + page size) is a plain object persisted to
 * localStorage via {@link useLocalStorage}. Rows are type-agnostic; the
 * "Szczegoly" column defers to the per-type JobSummary.
 */
import { useEffect, useState } from 'react'
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type PaginationState,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table'
import { ArrowUpDown, SlidersHorizontal } from 'lucide-react'
import type { Job } from '../../lib/schemas'
import { TESTID } from '../../lib/testids'
import { useLocalStorage } from '../../hooks/useLocalStorage'
import { jobColumns, DEFAULT_COLUMN_VISIBILITY } from './columns'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'
import { Button } from '../ui/button'
import { TablePagination } from '../TablePagination'

interface TableConfig {
  v: 1
  visibility: VisibilityState
  sorting: SortingState
  pageSize: number
}

const DEFAULT_CONFIG: TableConfig = {
  v: 1,
  visibility: DEFAULT_COLUMN_VISIBILITY,
  sorting: [{ id: 'created', desc: true }],
  pageSize: 25,
}

const PAGE_SIZES = [25, 50, 100]

export function JobsTable({
  jobs,
  onRowClick,
  resetKey,
}: {
  jobs: Job[]
  onRowClick: (id: string) => void
  /** Changing this string resets to page 1 (e.g. when filters change). */
  resetKey?: string
}) {
  const [config, setConfig] = useLocalStorage<TableConfig>(
    'rm.jobs.table',
    DEFAULT_CONFIG,
  )
  // Pagination is fully controlled (owned here) so a background poll's data
  // refresh can never yank the user off their current page; only a filter
  // change (resetKey) or a page-size change moves it.
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: config.pageSize,
  })

  const table = useReactTable({
    data: jobs,
    columns: jobColumns,
    state: {
      columnVisibility: config.visibility,
      sorting: config.sorting,
      pagination,
    },
    onPaginationChange: setPagination,
    onColumnVisibilityChange: (updater) =>
      setConfig((c) => ({
        ...c,
        visibility:
          typeof updater === 'function' ? updater(c.visibility) : updater,
      })),
    onSortingChange: (updater) =>
      setConfig((c) => ({
        ...c,
        sorting: typeof updater === 'function' ? updater(c.sorting) : updater,
      })),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  })

  // Reset to page 1 when the filter set changes (not on background polls).
  useEffect(() => {
    setPagination((p) => ({ ...p, pageIndex: 0 }))
  }, [resetKey])

  const pageCount = table.getPageCount()
  const pageIndex = pagination.pageIndex

  const setPageSize = (n: number) => {
    setPagination((p) => ({ ...p, pageIndex: 0, pageSize: n }))
    setConfig((c) => ({ ...c, pageSize: n }))
  }

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">
          {jobs.length} zadan
        </span>
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <Button
                variant="outline"
                size="sm"
                data-testid={TESTID.jobsTableColumns}
              >
                <SlidersHorizontal />
                Kolumny
              </Button>
            }
          />
          <DropdownMenuContent align="end">
            <DropdownMenuGroup>
              <DropdownMenuLabel>Kolumny</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {table
                .getAllColumns()
                .filter((col) => col.getCanHide())
                .map((col) => (
                  <DropdownMenuCheckboxItem
                    key={col.id}
                    checked={col.getIsVisible()}
                    onCheckedChange={(checked) =>
                      col.toggleVisibility(!!checked)
                    }
                  >
                    {(col.columnDef.meta as { label?: string })?.label ??
                      col.id}
                  </DropdownMenuCheckboxItem>
                ))}
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-border">
        <Table data-testid={TESTID.jobsTable}>
          <TableHeader>
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id}>
                {hg.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder ? null : header.column.getCanSort() ? (
                      <button
                        className="inline-flex items-center gap-1 hover:text-foreground"
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                        <ArrowUpDown className="size-3.5 opacity-50" />
                      </button>
                    ) : (
                      flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )
                    )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                data-testid={TESTID.jobRow(row.original.id)}
                className="cursor-pointer"
                onClick={() => onRowClick(row.original.id)}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <TablePagination
        pageIndex={pageIndex}
        pageCount={pageCount}
        pageSize={table.getState().pagination.pageSize}
        pageSizes={PAGE_SIZES}
        canPreviousPage={table.getCanPreviousPage()}
        canNextPage={table.getCanNextPage()}
        onPageSizeChange={setPageSize}
        onPreviousPage={() => table.previousPage()}
        onNextPage={() => table.nextPage()}
      />
    </div>
  )
}
